import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useUnifiedTranslation } from "../../hooks/useUnifiedTranslation";
import { formatMixedTotal, BASE_CURRENCY } from "../../utils/currency";
import { formatShortDate, toIsoUtc } from "../../utils/date-utils";
import { getOrganizerStatusColor } from "../../utils/status-mappers";
import { browserTimeZone } from "../../utils/events/agenda-item-time";
import { EventStatus, ParticipantStatus } from "../../types/events-unified";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Divider,
  Alert,
  Badge,
} from "@mui/material";
import {
  Event as EventIcon,
  People as PeopleIcon,
  Assignment as TaskIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  MonetizationOn as MoneyIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import {
  eventsApi,
  type CreateEventRequest,
} from "../../services/api/events.api";
import { useAuth } from "../../contexts/AuthContext";
import { usersApi } from "../../services/api/users.api";
import { tasksApi } from "../../services/api/tasks.api";
import { organizerApi } from "../../services/api/organizer.api";
import { DataTable, DataTableColumn } from "../../components/ui/DataTable";

// Типы данных для организатора
interface OrganizerEvent {
  id: string;
  title: string;
  date: string;
  location: string;
  status: "planning" | "active" | "completed" | "cancelled";
  participants: number;
}

interface Participant {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: "confirmed" | "pending" | "declined";
  role: string;
  avatar?: string;
}

// API Integration for Events

// API Integration for Participants

// API Integration for Tasks

// Компонент KPI карточки
const KPICard: React.FC<{
  title: string;
  value: string | number;
  change?: number | undefined;
  icon: React.ReactNode;
  color?: string;
  onClick?: () => void;
}> = ({ title, value, change, icon, color = "primary", onClick }) => (
  <Card
    sx={{ height: "100%", cursor: onClick ? "pointer" : "default" }}
    onClick={onClick}
  >
    <CardContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
          {change !== undefined && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              {change >= 0 ? (
                <TrendingUpIcon color="success" fontSize="small" />
              ) : (
                <TrendingDownIcon color="error" fontSize="small" />
              )}
              <Typography
                variant="body2"
                color={change >= 0 ? "success.main" : "error.main"}
                sx={{ ml: 0.5 }}
              >
                {change >= 0 ? "+" : ""}
                {change}%
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            width: 56,
            height: 56,
          }}
        >
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

// Компонент статуса (ts — хелпер из дашборда для string)
const STATUS_LABELS: Record<string, string> = {
  planning: "organizer.statuses.planning",
  active: "organizer.statuses.active",
  completed: "organizer.statuses.completed",
  cancelled: "organizer.statuses.cancelled",
  confirmed: "organizer.statuses.confirmed",
  pending: "organizer.statuses.pending",
  declined: "organizer.statuses.declined",
  todo: "organizer.statuses.todo",
  "in-progress": "organizer.statuses.inProgress",
  overdue: "organizer.statuses.overdue",
};

const STATUS_FALLBACKS: Record<string, string> = {
  planning: "Планирование",
  active: "Активно",
  completed: "Завершено",
  cancelled: "Отменено",
  confirmed: "Подтверждено",
  pending: "Ожидание",
  declined: "Отклонено",
  todo: "К выполнению",
  "in-progress": "В работе",
  overdue: "Просрочено",
};

const StatusChip: React.FC<{
  status: string;
  ts: (key: string, fallback: string) => string;
}> = ({ status, ts }) => {
  const i18nKey = STATUS_LABELS[status];
  const fallback = STATUS_FALLBACKS[status] ?? status;
  const label = i18nKey ? ts(i18nKey, fallback) : fallback;
  return (
    <Chip label={label} color={getOrganizerStatusColor(status)} size="small" />
  );
};

// Основной компонент
const OrganizerDashboard: React.FC = () => {
  const { t, currentLanguage: _currentLanguage } = useUnifiedTranslation({
    namespace: "dashboard",
  });
  // `t` возвращает САМ КЛЮЧ, когда перевода нет, поэтому проверка `typeof`
  // была истинной всегда, а запасная подпись не срабатывала никогда: на экране
  // оказывался `organizer.statuses.planning` вместо «Планирование». Запасную
  // подпись знает только i18next — через `defaultValue` (AD-472).
  const ts = (key: string, fallback: string): string =>
    String(t(key, { defaultValue: fallback }));
  const [currentTab, setCurrentTab] = useState(0);
  const [createEventDialog, setCreateEventDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formTime, setFormTime] = useState("");
  const [formLocation, setFormLocation] = useState("");
  const [formBudget, setFormBudget] = useState<number | "">("");
  const [formPriority, setFormPriority] = useState<"low" | "medium" | "high">(
    "medium",
  );
  const [formDescription, setFormDescription] = useState("");

  useAuth();
  const [_selectedEvent, _setSelectedEvent] = useState<OrganizerEvent | null>(
    null,
  );

  const queryClient = useQueryClient();

  // ADR-002: envelope.data — массив событий (пагинация в meta)
  const {
    data: eventsResponse,
    isLoading: eventsLoading,
    error: eventsError,
  } = useQuery({
    queryKey: ["organizer", "events"],
    queryFn: () => eventsApi.getEvents(),
  });

  const events = useMemo(() => eventsResponse?.data ?? [], [eventsResponse]);

  // ADR-002: envelope.data — массив пользователей (пагинация в meta)
  const { data: usersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ["organizer", "users"],
    queryFn: () => usersApi.getUsers(),
  });

  const users = useMemo(() => usersResponse?.data ?? [], [usersResponse]);

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ["organizer", "tasks"],
    queryFn: () => tasksApi.getTasks(),
  });

  /**
   * EUR-22: бюджет организатора — отдельный агрегат, а не поле события.
   *
   * У сущности события колонки бюджета нет вовсе: бюджет живёт в `event_budget_items`
   * и суммируется бэкендом по всем событиям организатора — раздельно по валютам
   * (валюта задаётся событием, события могут быть в разных). Поэтому здесь список
   * `totals`, а печать идёт через `formatMixedTotal`, а не через одно число.
   */
  const { data: budgetResponse, isLoading: budgetLoading } = useQuery({
    queryKey: ["organizer", "budget-overview"],
    queryFn: () => organizerApi.getBudgetOverview(),
  });

  const budgetTotals = useMemo(
    () => budgetResponse?.data?.totals ?? [],
    [budgetResponse],
  );

  // Transform API data to component format с проверкой массива
  const organizerEvents: OrganizerEvent[] = Array.isArray(events)
    ? (events as any[]).map((ev) => ({
        id: ev.id,
        title: ev.title || "Без названия",
        date: ev.startDate || new Date().toISOString().split("T")[0],
        location: ev.location || "Не указано",
        status:
          ev.status === "draft"
            ? "planning"
            : ev.status === "published"
              ? "active"
              : ev.status === "completed"
                ? "completed"
                : ev.status === "cancelled"
                  ? "cancelled"
                  : "planning",
        participants: ev.currentParticipants || ev.maxParticipants || 0,
        // EUR-22: полей `budget`/`completion`/`priority` в контракте события НЕТ.
        // Раньше сюда писались нули и `"medium"` «по умолчанию», и дальше они
        // складывались и печатались как настоящие цифры. Место, где данных нет,
        // обязано молчать: бюджет теперь берётся из своего эндпоинта (см. ниже),
        // прогресс и приоритет события источника не имеют и с экрана сняты.
      }))
    : [];

  const participants: Participant[] = Array.isArray(users)
    ? users.map((user) => ({
        id: user.id,
        name:
          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
          "Без имени",
        email: user.email || "",
        phone: user.phone || "",
        status: ParticipantStatus.CONFIRMED,
        role: user.role || "Участник",
      }))
    : [];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Loading states
  if (eventsLoading || usersLoading || tasksLoading || budgetLoading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Typography variant="h6">
          {ts(
            "organizer.loading.organizerData",
            "Загрузка данных организатора...",
          )}
        </Typography>
      </Box>
    );
  }

  // Error handling
  if (eventsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Ошибка загрузки событий:{" "}
          {eventsError instanceof Error
            ? eventsError.message
            : "Неизвестная ошибка"}
        </Alert>
      </Box>
    );
  }

  // Calculations based on real data
  // EUR-22: суммы бюджета приходят с сервера уже разложенными по валютам.
  // `hasBudgetData` отделяет «позиций бюджета нет» от «бюджет равен нулю» —
  // раньше оба случая выглядели как уверенное «0 €».
  const hasBudgetData = budgetTotals.length > 0;
  const budgetMoneyItems = budgetTotals.map((t) => ({
    amount: t.totalBudget,
    currency: t.currency,
  }));
  const totalParticipants = organizerEvents.reduce(
    (sum, event) => sum + event.participants,
    0,
  );
  const completedTasks = Array.isArray(tasks)
    ? tasks.filter((task) => task.status === "completed").length
    : 0;

  // Month-over-month change calculations from real data
  const _now = new Date();
  const _curM = _now.getMonth();
  const _curY = _now.getFullYear();
  const _prevM = _curM === 0 ? 11 : _curM - 1;
  const _prevY = _curM === 0 ? _curY - 1 : _curY;

  const _inMonth = (
    dateStr: string | undefined | null,
    m: number,
    y: number,
  ): boolean => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return !isNaN(d.getTime()) && d.getMonth() === m && d.getFullYear() === y;
  };

  const _pctChange = (cur: number, prev: number): number | undefined => {
    if (prev > 0) return Math.round(((cur - prev) / prev) * 100);
    if (cur > 0) return 100;
    return undefined;
  };

  // Active events: compare by event startDate month
  const _activeNow = organizerEvents.filter(
    (e) =>
      (e.status === "active" || e.status === "planning") &&
      _inMonth(e.date, _curM, _curY),
  ).length;
  const _activePrev = organizerEvents.filter(
    (e) =>
      (e.status === "active" || e.status === "planning") &&
      _inMonth(e.date, _prevM, _prevY),
  ).length;
  const activeEventsChange = _pctChange(_activeNow, _activePrev);

  // Participants: sum for events whose startDate falls in the month
  const _partNow = organizerEvents
    .filter((e) => _inMonth(e.date, _curM, _curY))
    .reduce((sum, e) => sum + e.participants, 0);
  const _partPrev = organizerEvents
    .filter((e) => _inMonth(e.date, _prevM, _prevY))
    .reduce((sum, e) => sum + e.participants, 0);
  const participantsChange = _pctChange(_partNow, _partPrev);

  // Completed tasks: use updatedAt or createdAt from raw API response
  const _rawTasks = Array.isArray(tasks) ? (tasks as any[]) : [];
  const _doneNow = _rawTasks.filter(
    (t) =>
      t.status === "completed" &&
      _inMonth(t.updatedAt ?? t.createdAt, _curM, _curY),
  ).length;
  const _donePrev = _rawTasks.filter(
    (t) =>
      t.status === "completed" &&
      _inMonth(t.updatedAt ?? t.createdAt, _prevM, _prevY),
  ).length;
  const completedTasksChange = _pctChange(_doneNow, _donePrev);

  // Budget: always 0 from current API mapping — no change indicator

  // Колонки таблицы мероприятий
  const eventColumns: DataTableColumn<OrganizerEvent>[] = [
    {
      id: "title",
      label: ts("organizer.columns.title", "Название"),
      minWidth: 180,
    },
    {
      id: "date",
      label: ts("organizer.columns.date", "Дата"),
      minWidth: 110,
      render: (v) => formatShortDate(String(v)),
    },
    {
      id: "location",
      label: ts("organizer.columns.location", "Место"),
      minWidth: 150,
    },
    {
      id: "status",
      label: ts("organizer.columns.status", "Статус"),
      minWidth: 120,
      render: (v) => <StatusChip status={String(v)} ts={ts} />,
    },
    {
      id: "participants",
      label: ts("organizer.columns.participants", "Участники"),
      minWidth: 120,
      render: (v) => (
        <Badge badgeContent={Number(v)} color="primary">
          <PeopleIcon />
        </Badge>
      ),
    },
    // EUR-22: колонка «Выполнение» снята. Она рисовала полосу прогресса по
    // значению, которого в контракте события нет: полоса всегда была пустой, а
    // подпись всегда «0 %» — это не «нет данных», это уверенно неверная цифра.
  ];

  // Данные и колонки для участников
  const participantColumns: DataTableColumn<Participant>[] = [
    { id: "name", label: ts("organizer.columns.name", "Имя"), minWidth: 180 },
    {
      id: "email",
      label: ts("organizer.columns.email", "Email"),
      minWidth: 200,
    },
    {
      id: "phone",
      label: ts("organizer.columns.phone", "Телефон"),
      minWidth: 140,
    },
    { id: "role", label: ts("organizer.columns.role", "Роль"), minWidth: 120 },
    {
      id: "status",
      label: ts("organizer.columns.status", "Статус"),
      minWidth: 120,
      render: (v) => <StatusChip status={String(v)} ts={ts} />,
    },
  ];

  // Данные и колонки для задач
  interface ApiTask {
    id: string;
    title: string;
    description?: string;
    assignee?: { name?: string };
    dueDate?: string;
    status?: string;
    priority?: string;
  }
  const taskRows = Array.isArray(tasks)
    ? (tasks as ApiTask[]).map((t) => ({
        id: t.id,
        title: t.title,
        description: t.description,
        assigneeName: t.assignee?.name || "—",
        dueDate: t.dueDate,
        status: t.status || "todo",
        priority: t.priority || "medium",
      }))
    : [];

  const taskColumns: DataTableColumn<(typeof taskRows)[number]>[] = [
    {
      id: "title",
      label: ts("organizer.columns.title", "Название"),
      minWidth: 220,
      render: (_v, r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {r.title}
          </Typography>
          {r.description && (
            <Typography variant="caption" color="text.secondary">
              {r.description.substring(0, 90)}
              {r.description?.length > 90 ? "…" : ""}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: "assigneeName",
      label: ts("organizer.columns.assigneeName", "Исполнитель"),
      minWidth: 160,
    },
    {
      id: "dueDate",
      label: ts("organizer.columns.dueDate", "Срок"),
      minWidth: 120,
      render: (v) => (v ? formatShortDate(String(v)) : "—"),
    },
    {
      id: "status",
      label: ts("organizer.columns.status", "Статус"),
      minWidth: 120,
      render: (v) => <StatusChip status={String(v)} ts={ts} />,
    },
    {
      id: "priority",
      label: ts("organizer.columns.priority", "Приоритет"),
      minWidth: 120,
      render: (v) => <Chip label={String(v)} size="small" />,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {ts("organizer.title", "Панель организатора")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateEventDialog(true)}
          >
            {ts("organizer.createEvent", "Создать событие")}
          </Button>
          <IconButton>
            <RefreshIcon />
          </IconButton>
          <IconButton>
            <NotificationsIcon />
          </IconButton>
        </Box>
      </Box>

      {/* KPI Карточки */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={ts("organizer.kpis.activeEvents", "Активные события")}
            value={
              organizerEvents.filter(
                (e) => e.status === "active" || e.status === "planning",
              ).length
            }
            change={activeEventsChange}
            icon={<EventIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={ts("organizer.kpis.totalParticipants", "Всего участников")}
            value={totalParticipants}
            change={participantsChange}
            icon={<PeopleIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={ts("organizer.kpis.completedTasks", "Выполнено задач")}
            value={`${completedTasks}/${tasks.length}`}
            change={completedTasksChange}
            icon={<TaskIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={ts("organizer.kpis.totalBudget", "Общий бюджет")}
            value={
              hasBudgetData
                ? formatMixedTotal(budgetMoneyItems)
                : ts("organizer.kpis.noBudgetData", "Нет данных")
            }
            icon={<MoneyIcon />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Основной контент с вкладками */}
      <Card>
        <CardHeader
          title={
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab label={ts("organizer.tabs.events", "События")} />
              <Tab label={ts("organizer.tabs.participants", "Участники")} />
              <Tab label={ts("organizer.tabs.tasks", "Задачи")} />
              <Tab label={ts("organizer.tabs.analytics", "Аналитика")} />
            </Tabs>
          }
        />
        <Divider />
        <CardContent>
          {/* Вкладка: Мероприятия */}
          {currentTab === 0 && (
            <DataTable
              data={organizerEvents}
              columns={eventColumns}
              rowKey={(r) => r.id}
              title={ts("organizer.myEvents", "Мои события")}
              searchable
              filterable
              exportable
              pagination
            />
          )}

          {/* Вкладка: Участники */}
          {currentTab === 1 && (
            <DataTable
              data={participants}
              columns={participantColumns}
              rowKey={(r) => r.id}
              title={ts("organizer.tabs.participants", "Участники")}
              searchable
              filterable
              exportable
              pagination
            />
          )}

          {/* Вкладка: Задачи */}
          {currentTab === 2 && (
            <DataTable
              data={taskRows}
              columns={taskColumns}
              rowKey={(r) => r.id}
              title={ts("organizer.tabs.tasks", "Задачи")}
              searchable
              filterable
              exportable
              pagination
            />
          )}

          {/* Вкладка: Аналитика */}
          {currentTab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {ts("organizer.eventStatistics", "Статистика событий")}
                  </Typography>
                  <Typography variant="body2">
                    {ts("organizer.activeEvents", "Активные")}:{" "}
                    {
                      organizerEvents.filter((e) => e.status === "active")
                        .length
                    }
                  </Typography>
                  <Typography variant="body2">
                    {ts("organizer.inPlanning", "В планировании")}:{" "}
                    {
                      organizerEvents.filter((e) => e.status === "planning")
                        .length
                    }
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {ts("organizer.financialAnalytics", "Финансовая аналитика")}
                  </Typography>
                  <Typography variant="body2">
                    {ts("organizer.totalBudget", "Общий бюджет")}:{" "}
                    {hasBudgetData
                      ? formatMixedTotal(budgetMoneyItems)
                      : ts("organizer.kpis.noBudgetData", "Нет данных")}
                  </Typography>
                  <Typography variant="body2">
                    {ts(
                      "organizer.averageBudgetPerEvent",
                      "Средний бюджет на событие",
                    )}
                    :{" "}
                    {hasBudgetData && organizerEvents.length > 0
                      ? formatMixedTotal(
                          budgetTotals.map((t) => ({
                            amount: t.totalBudget / organizerEvents.length,
                            currency: t.currency,
                          })),
                        )
                      : ts("organizer.kpis.noBudgetData", "Нет данных")}
                  </Typography>
                  <Typography variant="body2">
                    {ts("organizer.costPerParticipant", "Расход на участника")}:{" "}
                    {/* EUR-22: делитель проверяется. Прежний код делил на
                        `totalParticipants` без проверки — при нуле участников
                        на экран уходило `Infinity`/`NaN`. И слагаемое взято
                        `spentBudget` (факт), а не план: подпись говорит
                        «расход», а печатался плановый бюджет. */}
                    {hasBudgetData && totalParticipants > 0
                      ? formatMixedTotal(
                          budgetTotals.map((t) => ({
                            amount: t.spentBudget / totalParticipants,
                            currency: t.currency,
                          })),
                        )
                      : ts("organizer.kpis.noBudgetData", "Нет данных")}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Диалог создания события */}
      <Dialog
        open={createEventDialog}
        onClose={() => setCreateEventDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Создать новое мероприятие</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название мероприятия"
                variant="outlined"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Дата"
                type="date"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Время"
                type="time"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={formTime}
                onChange={(e) => setFormTime(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Место проведения"
                variant="outlined"
                value={formLocation}
                onChange={(e) => setFormLocation(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={`Бюджет (${BASE_CURRENCY})`}
                type="number"
                variant="outlined"
                value={formBudget}
                onChange={(e) =>
                  setFormBudget(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Приоритет</InputLabel>
                <Select
                  label="Приоритет"
                  value={formPriority}
                  onChange={(e) =>
                    setFormPriority(e.target.value as "low" | "medium" | "high")
                  }
                >
                  <MenuItem value="low">Низкий</MenuItem>
                  <MenuItem value="medium">Средний</MenuItem>
                  <MenuItem value="high">Высокий</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Описание"
                multiline
                rows={4}
                variant="outlined"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateEventDialog(false)}>Отмена</Button>
          <Button
            variant="contained"
            disabled={creating}
            onClick={async () => {
              try {
                setCreating(true);
                setCreateError(null);
                if (!formTitle || !formDate) {
                  setCreateError("Заполните как минимум название и дату.");
                  setCreating(false);
                  return;
                }
                const startIso = toIsoUtc(formDate, formTime);
                const endIso = new Date(
                  new Date(startIso).getTime() + 2 * 60 * 60 * 1000,
                )
                  .toISOString()
                  .replace(".000Z", "Z");
                // AD-527: пояс уходит, только если это имя IANA — иначе сервер
                // отвергнет всё создание (400). Без пояса событие создаётся.
                const timezone = browserTimeZone();
                const createPayload: CreateEventRequest = {
                  title: formTitle,
                  description: formDescription || "",
                  startDate: startIso,
                  endDate: endIso,
                  type: "conference",
                  status: EventStatus.DRAFT,
                  visibility: "public",
                  maxParticipants: 0,
                  tags: [],
                  ...(timezone ? { timezone } : {}), // A5: зона пользователя, не Москва
                  // Н-6: `isPublic: true` сняли — публичность уже задана
                  // `visibility: "public"` выше; `requiresApproval` и
                  // `allowGuestRegistration` бэкенду неизвестны.
                };
                await eventsApi.createEvent(createPayload);
                setCreateEventDialog(false);
                await queryClient.invalidateQueries({
                  queryKey: ["organizer", "events"],
                });
              } catch (e: unknown) {
                const err = e as {
                  details?: { status?: number; url?: string };
                  status?: number;
                  config?: { url?: string };
                  message?: string;
                };
                const code = err?.details?.status ?? err?.status;
                const url = err?.details?.url ?? err?.config?.url;
                setCreateError(
                  code
                    ? `${code} при запросе ${url}`
                    : (err?.message ?? "Ошибка создания"),
                );
              } finally {
                setCreating(false);
              }
            }}
          >
            {creating ? "Создание…" : "Создать"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizerDashboard;
